import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './News.css';
import { getNewsById, getNews, getNewsArchives, getNewsCategories, getFileUrl, convertYouTubeUrl, getYouTubeThumbnail, getComments, createComment, toggleCommentLike, checkCommentLike } from '../services/apiService';

const NewsDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [similarPosts, setSimilarPosts] = useState([]);
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentName, setCommentName] = useState('');
  const [commentEmail, setCommentEmail] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyName, setReplyName] = useState('');
  const [replyEmail, setReplyEmail] = useState('');
  const [commentEmailError, setCommentEmailError] = useState(false);
  const [replyEmailError, setReplyEmailError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSimilarPage, setCurrentSimilarPage] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archives, setArchives] = useState([]);
  const [expandedReplies, setExpandedReplies] = useState({}); // Pour suivre quels commentaires ont leurs réponses développées
  const [categories, setCategories] = useState([]);
  const [notification, setNotification] = useState(null);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fonction pour transformer les données de l'API vers le format attendu par le frontend
  const transformNewsData = (newsData) => {
    // Obtenir l'image (image_url ou image_file_path ou premier média image)
    let imageUrl = newsData.image_url;
    if (!imageUrl && newsData.image_file_path) {
      imageUrl = getFileUrl(newsData.image_file_path);
    }
    if (!imageUrl && newsData.media && newsData.media.length > 0) {
      const firstImage = newsData.media.find(m => m.media_type === 'image');
      if (firstImage) {
        imageUrl = firstImage.url ? getFileUrl(firstImage.url) : getFileUrl(firstImage.file_path);
      }
    }
    
    // Construire la galerie à partir des médias
    const gallery = (newsData.media || []).map(media => {
      // Construire l'URL : priorité à url, puis file_path
      let mediaUrl = null;
      if (media.url) {
        mediaUrl = getFileUrl(media.url);
      } else if (media.file_path) {
        mediaUrl = getFileUrl(media.file_path);
      }
      
      // Pour les vidéos YouTube, obtenir le thumbnail si non disponible
      let thumbnail = null;
      if (media.thumbnail_url) {
        thumbnail = getFileUrl(media.thumbnail_url);
      } else if (media.media_type === 'video' && mediaUrl && (mediaUrl.includes('youtube.com') || mediaUrl.includes('youtu.be'))) {
        // Générer le thumbnail YouTube
        thumbnail = getYouTubeThumbnail(mediaUrl);
      } else if (media.media_type === 'video' && mediaUrl) {
        // Pour les vidéos uploadées, utiliser l'URL de la vidéo comme fallback
        thumbnail = mediaUrl;
      }
      
      return {
        type: media.media_type,
        url: mediaUrl,
        thumbnail: thumbnail
      };
    }).filter(media => media.url !== null); // Filtrer les médias sans URL valide

    return {
      id: newsData.id,
      title: newsData.title,
      date: newsData.created_at || newsData.date,
      categories: newsData.categories || [],
      href: `/news/${newsData.id}`,
      img: imageUrl || 'https://via.placeholder.com/400x300?text=News',
      excerpt: newsData.content ? newsData.content.substring(0, 150) + '...' : '',
      content: newsData.content || '',
      author: newsData.author_name || newsData.author || 'Admin',
      readTime: Math.ceil((newsData.content || '').length / 1000) || 5,
      views: newsData.views || 0,
      featured: false,
      gallery: gallery
    };
  };

  // Ancien mock data - maintenant remplacé par les données de l'API
  const oldPosts = [
    {
      id: 1,
      title: 'Eco-Conscious Eating: The USDA wants us to dine greener',
      date: '2023-08-10',
      categories: ['sustainability', 'innovation'],
      href: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/2023/08/10/eco-conscious-eating-the-usda-wants-us-to-dine-greener/',
      img: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-2-640x506.jpg',
      excerpt: 'For more than 100 years, the USDA has published nutrition guidelines to help Americans make healthy food choices. Now, they\'re taking it a step further by considering the environmental impact of our food choices.',
      content: `For more than 100 years, the USDA has published nutrition guidelines to help Americans make healthy food choices. Now, they're taking it a step further by considering the environmental impact of our food choices.

The new guidelines, released in 2023, mark a significant shift in how we think about nutrition. Not only do they focus on what's healthy for our bodies, but they also consider what's healthy for our planet.

This comprehensive approach to dietary guidelines reflects growing awareness of the interconnectedness between human health and environmental sustainability. The USDA's initiative represents a forward-thinking approach to public health policy.

Key highlights of the new guidelines include:

1. **Plant-Based Emphasis**: Increased recommendations for plant-based foods, which generally have lower environmental footprints
2. **Local Sourcing**: Encouragement to choose locally sourced foods when possible
3. **Seasonal Eating**: Promotion of seasonal eating patterns that align with natural growing cycles
4. **Reduced Food Waste**: Strategies for minimizing food waste at the household level

The implementation of these guidelines will require collaboration between policymakers, healthcare providers, and the food industry. Educational campaigns will be crucial to help consumers understand and adopt these new recommendations.

As we move forward, it's clear that sustainable nutrition is not just a trend but a necessary evolution in how we approach food and health.`,
      author: 'Dr. Sarah Green',
      readTime: 5,
      views: 1247,
      featured: true,
      gallery: [
        {
          type: 'image',
          url: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-2-640x506.jpg'
        },
        {
          type: 'image',
          url: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-3-640x506.jpg'
        },
        {
          type: 'video',
          url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-4-640x506.jpg'
        }
      ]
    },
    {
      id: 2,
      title: 'TerraBrands Launches to Expand Edible Algae Category',
      date: '2023-08-15',
      categories: ['innovation', 'technology'],
      href: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/2023/08/15/terrabrands-launches-to-expand-edible-algae-category/',
      img: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-3-640x506.jpg',
      excerpt: 'TerraBrands, a pioneering food technology company, has announced the launch of their revolutionary edible algae product line, promising to revolutionize the sustainable food industry.',
      content: `TerraBrands, a pioneering food technology company, has announced the launch of their revolutionary edible algae product line, promising to revolutionize the sustainable food industry.

The company's innovative approach to algae cultivation and processing has resulted in products that are not only nutritious but also environmentally sustainable. Their proprietary technology allows for efficient production while maintaining the highest quality standards.

The edible algae category represents a significant opportunity for growth in the food industry. With increasing consumer awareness about sustainability and nutrition, algae-based products are gaining traction as a viable alternative to traditional food sources.

TerraBrands' product line includes:

- **Protein-Rich Algae Powders**: Perfect for smoothies and supplements
- **Algae-Based Snacks**: Healthy alternatives to traditional snacks
- **Culinary Algae Products**: Versatile ingredients for home cooking
- **Functional Algae Beverages**: Nutrient-dense drinks for active lifestyles

The company's commitment to sustainability extends beyond their products. Their production facilities are powered by renewable energy, and they use closed-loop water systems to minimize environmental impact.

This launch represents a significant milestone in the evolution of sustainable food technology and positions TerraBrands as a leader in the emerging algae-based food market.`,
      author: 'Michael Chen',
      readTime: 7,
      views: 892,
      featured: false,
      gallery: [
        {
          type: 'image',
          url: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-3-640x506.jpg'
        }
      ]
    },
    {
      id: 3,
      title: 'Revolutionary Agricultural Technology Transforms Farming Practices',
      date: '2023-08-20',
      categories: ['agriculture', 'technology'],
      href: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/2023/08/20/revolutionary-agricultural-technology-transforms-farming-practices/',
      img: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-4-640x506.jpg',
      excerpt: 'A groundbreaking agricultural technology has been developed that promises to increase crop yields while reducing environmental impact, marking a new era in sustainable farming.',
      content: `A groundbreaking agricultural technology has been developed that promises to increase crop yields while reducing environmental impact, marking a new era in sustainable farming.

The innovative system combines precision agriculture techniques with advanced AI algorithms to optimize farming operations. This technology enables farmers to make data-driven decisions that improve both productivity and sustainability.

Key features of this revolutionary technology include:

1. **Smart Irrigation Systems**: Automated watering based on real-time soil moisture data
2. **Predictive Analytics**: AI-powered insights for crop management
3. **Drone Technology**: Aerial monitoring for crop health assessment
4. **Soil Health Monitoring**: Continuous analysis of soil conditions

The implementation of this technology has shown remarkable results in pilot programs across different regions. Farmers have reported:

- 25% increase in crop yields
- 30% reduction in water usage
- 40% decrease in pesticide application
- Improved soil health indicators

The technology is designed to be accessible to farmers of all sizes, with scalable solutions that can be adapted to different farming operations. Training programs are available to help farmers integrate these new tools into their existing practices.

This development represents a significant step forward in the quest for sustainable agriculture and food security.`,
      author: 'Dr. Maria Rodriguez',
      readTime: 6,
      views: 1156,
      featured: true,
      gallery: [
        {
          type: 'image',
          url: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-4-640x506.jpg'
        },
        {
          type: 'image',
          url: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-5-640x506.jpg'
        }
      ]
    },
    {
      id: 4,
      title: 'Sustainable Packaging Solutions Gain Momentum in Food Industry',
      date: '2023-08-25',
      categories: ['sustainability'],
      href: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/2023/08/25/sustainable-packaging-solutions-gain-momentum-in-food-industry/',
      img: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-5-640x506.jpg',
      excerpt: 'Food companies are increasingly adopting sustainable packaging solutions to reduce environmental impact and meet consumer demands for eco-friendly products.',
      content: `Food companies are increasingly adopting sustainable packaging solutions to reduce environmental impact and meet consumer demands for eco-friendly products.

The shift towards sustainable packaging represents a fundamental change in how the food industry approaches product design and distribution. Companies are investing heavily in research and development to create packaging solutions that are both functional and environmentally responsible.

Current trends in sustainable packaging include:

- **Biodegradable Materials**: Plant-based packaging that breaks down naturally
- **Recyclable Solutions**: Packaging designed for easy recycling
- **Minimal Packaging**: Reduced packaging approaches
- **Edible Packaging**: Innovative solutions that can be consumed

Major food companies are setting ambitious targets for sustainable packaging adoption. These initiatives are driven by both regulatory requirements and consumer preferences for environmentally conscious products.

The benefits of sustainable packaging extend beyond environmental impact:

1. **Brand Differentiation**: Companies can distinguish themselves in the market
2. **Cost Savings**: Long-term savings through efficient material use
3. **Regulatory Compliance**: Meeting evolving environmental regulations
4. **Consumer Appeal**: Attracting environmentally conscious consumers

The implementation of sustainable packaging solutions requires collaboration across the entire supply chain, from material suppliers to retailers.`,
      author: 'Jennifer Lee',
      readTime: 4,
      views: 743,
      featured: false,
      gallery: []
    },
    {
      id: 5,
      title: 'Innovation in Vertical Farming Revolutionizes Urban Agriculture',
      date: '2023-09-01',
      categories: ['agriculture', 'innovation'],
      href: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/2023/09/01/innovation-in-vertical-farming-revolutionizes-urban-agriculture/',
      img: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-6-640x506.jpg',
      excerpt: 'Vertical farming technology is transforming urban agriculture, enabling cities to produce fresh food locally while reducing transportation costs and environmental impact.',
      content: `Vertical farming technology is transforming urban agriculture, enabling cities to produce fresh food locally while reducing transportation costs and environmental impact.

The concept of vertical farming involves growing crops in vertically stacked layers, often in controlled environments such as buildings or shipping containers. This approach maximizes space efficiency and allows for year-round production regardless of weather conditions.

Key advantages of vertical farming include:

1. **Space Efficiency**: Higher crop yields per square foot
2. **Water Conservation**: Up to 95% less water usage than traditional farming
3. **Pesticide-Free**: Controlled environments eliminate the need for pesticides
4. **Year-Round Production**: Consistent harvests regardless of season
5. **Reduced Transportation**: Local production reduces food miles

Recent innovations in vertical farming technology have made it more accessible and cost-effective:

- **LED Lighting Systems**: Energy-efficient grow lights optimized for plant growth
- **Automated Systems**: Robotics for planting, watering, and harvesting
- **Climate Control**: Precise temperature and humidity management
- **Data Analytics**: AI-powered optimization of growing conditions

Urban vertical farms are becoming increasingly common in major cities worldwide. These facilities are not only producing food but also serving as educational centers and community hubs.

The future of vertical farming looks promising, with continued technological advances making it more efficient and sustainable.`,
      author: 'Robert Kim',
      readTime: 8,
      views: 1567,
      featured: true,
      gallery: [
        {
          type: 'image',
          url: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-6-640x506.jpg'
        },
        {
          type: 'video',
          url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          thumbnail: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-7-640x506.jpg'
        },
        {
          type: 'image',
          url: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-8-640x506.jpg'
        }
      ]
    },
    {
      id: 6,
      title: 'Smart Agriculture: IoT Solutions Transform Traditional Farming',
      date: '2023-09-05',
      categories: ['agriculture', 'technology'],
      href: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/2023/09/05/smart-agriculture-iot-solutions-transform-traditional-farming/',
      img: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-7-640x506.jpg',
      excerpt: 'Internet of Things (IoT) technology is revolutionizing traditional farming practices, enabling farmers to monitor and optimize their operations with unprecedented precision.',
      content: `Internet of Things (IoT) technology is revolutionizing traditional farming practices, enabling farmers to monitor and optimize their operations with unprecedented precision.

Smart agriculture systems use sensors, connectivity, and data analytics to provide real-time insights into farm operations. This technology enables farmers to make informed decisions that improve efficiency and sustainability.

Core components of smart agriculture include:

1. **Soil Sensors**: Monitor moisture, pH, and nutrient levels
2. **Weather Stations**: Track local weather conditions
3. **Crop Monitoring**: Visual and sensor-based crop health assessment
4. **Livestock Tracking**: Monitor animal health and behavior
5. **Equipment Management**: Track machinery performance and maintenance

The benefits of IoT in agriculture are substantial:

- **Increased Efficiency**: Optimized resource usage and reduced waste
- **Better Decision Making**: Data-driven insights for farm management
- **Cost Reduction**: Lower input costs through precision application
- **Environmental Benefits**: Reduced chemical usage and water consumption
- **Improved Yields**: Better crop management leading to higher productivity

Implementation of smart agriculture technology requires investment in infrastructure and training. However, the long-term benefits often justify the initial costs.

The future of agriculture is increasingly digital, with IoT technology playing a central role in the evolution of farming practices.`,
      author: 'Dr. Ahmed Hassan',
      readTime: 6,
      views: 923,
      featured: false,
      gallery: [
        {
          type: 'image',
          url: 'https://ld-wp73.template-help.com/wordpress/prod_8771/v3/wp-content/uploads/2016/08/blog-7-640x506.jpg'
        }
      ]
    },
    {
      id: 7,
      title: 'Technologies vertes pour l\'agriculture',
      excerpt: 'Découvrez les nouvelles technologies vertes qui révolutionnent l\'agriculture moderne.',
      content: 'Les technologies vertes transforment l\'agriculture moderne. Ces innovations permettent de réduire l\'impact environnemental tout en améliorant la productivité. Les agriculteurs adoptent des solutions durables pour l\'avenir.',
      img: 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      date: '2023-08-20',
      author: 'Prof. Michael Green',
      categories: ['technology', 'sustainability'],
      readTime: '5 min',
      views: 756,
      featured: false,
      gallery: []
    },
    {
      id: 8,
      title: 'Agriculture durable en Méditerranée',
      excerpt: 'Les pratiques agricoles durables se développent dans la région méditerranéenne.',
      content: 'L\'agriculture durable prend de l\'ampleur en Méditerranée. Les agriculteurs adoptent des pratiques respectueuses de l\'environnement pour préserver les ressources naturelles. Cette approche garantit un avenir meilleur pour les générations futures.',
      img: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      date: '2023-08-25',
      author: 'Dr. Elena Rodriguez',
      categories: ['sustainability', 'agriculture'],
      readTime: '6 min',
      views: 892,
      featured: false,
      gallery: []
    },
    {
      id: 9,
      title: 'Innovations agricoles du futur',
      excerpt: 'Les innovations agricoles du futur transforment déjà nos pratiques actuelles.',
      content: 'Les innovations agricoles du futur sont déjà présentes aujourd\'hui. L\'intelligence artificielle, les drones et les capteurs IoT révolutionnent l\'agriculture. Ces technologies permettent une agriculture plus précise et durable.',
      img: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      date: '2023-08-30',
      author: 'Dr. Ahmed Hassan',
      categories: ['innovation', 'technology'],
      readTime: '7 min',
      views: 634,
      featured: false,
      gallery: []
    },
    {
      id: 10,
      title: 'Solutions agricoles intelligentes',
      excerpt: 'Les solutions agricoles intelligentes optimisent la production et réduisent les coûts.',
      content: 'Les solutions agricoles intelligentes utilisent l\'intelligence artificielle pour optimiser la production agricole. Ces systèmes permettent de réduire les coûts tout en améliorant la qualité des récoltes.',
      img: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      date: '2023-09-01',
      author: 'Dr. Sarah Wilson',
      categories: ['innovation', 'agriculture'],
      readTime: '4 min',
      views: 445,
      featured: false,
      gallery: []
    }
  ];

  useEffect(() => {
    const fetchNewsDetail = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Récupérer la news par ID depuis l'API
        const newsData = await getNewsById(id);
        if (newsData) {
          const transformedPost = transformNewsData(newsData);
          setPost(transformedPost);
          
          // Récupérer les autres news pour les posts récents et similaires
          const allNews = await getNews({ status: 'Publié' });
          const transformedAllNews = allNews.map(transformNewsData);
          
      // Get recent posts with images (excluding current post, sorted by date descending)
          const recentPosts = transformedAllNews
            .filter(p => p.id !== transformedPost.id && p.img)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 5);
      setRelatedPosts(recentPosts);
      
      // Get similar posts (same categories, excluding current post)
          // Comparer les catégories en normalisant en minuscules pour une comparaison fiable
          const currentPostCategories = (transformedPost.categories || []).map(cat => {
            // Gérer les catégories qui peuvent être des chaînes ou des objets
            const catName = typeof cat === 'string' ? cat : (cat.category || cat.name || cat);
            return (catName || '').toLowerCase().trim();
          }).filter(cat => cat.length > 0); // Filtrer les catégories vides
          
          let similar = [];
          
          if (currentPostCategories.length > 0) {
            // Filtrer les news qui ont au moins une catégorie en commun
            similar = transformedAllNews
              .filter(p => {
                if (p.id === transformedPost.id) return false;
                
                // Normaliser les catégories du post pour la comparaison
                const postCategories = (p.categories || []).map(cat => {
                  const catName = typeof cat === 'string' ? cat : (cat.category || cat.name || cat);
                  return (catName || '').toLowerCase().trim();
                }).filter(cat => cat.length > 0);
                
                // Vérifier si au moins une catégorie correspond
                return postCategories.some(postCat => 
                  currentPostCategories.includes(postCat)
                );
              })
              .sort((a, b) => {
                // Trier par nombre de catégories communes (plus de correspondances en premier)
                const aCategories = (a.categories || []).map(cat => {
                  const catName = typeof cat === 'string' ? cat : (cat.category || cat.name || cat);
                  return (catName || '').toLowerCase().trim();
                }).filter(cat => cat.length > 0);
                const bCategories = (b.categories || []).map(cat => {
                  const catName = typeof cat === 'string' ? cat : (cat.category || cat.name || cat);
                  return (catName || '').toLowerCase().trim();
                }).filter(cat => cat.length > 0);
                
                const aMatches = aCategories.filter(cat => currentPostCategories.includes(cat)).length;
                const bMatches = bCategories.filter(cat => currentPostCategories.includes(cat)).length;
                
                if (bMatches !== aMatches) {
                  return bMatches - aMatches; // Plus de correspondances en premier
                }
                
                // Si même nombre de correspondances, trier par date (plus récent en premier)
                return new Date(b.date) - new Date(a.date);
              })
        .slice(0, 5);
          } else {
            // Si pas de catégories, afficher les news les plus récentes (sauf la news actuelle)
            similar = transformedAllNews
              .filter(p => p.id !== transformedPost.id)
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .slice(0, 5);
          }
          
      setSimilarPosts(similar);
      
          // Récupérer les archives depuis la base de données
          const archivesData = await getNewsArchives({ status: 'Publié' });
          // Reformater les labels avec les traductions des mois
          const monthNames = [
            t('news.months.january'),
            t('news.months.february'),
            t('news.months.march'),
            t('news.months.april'),
            t('news.months.may'),
            t('news.months.june'),
            t('news.months.july'),
            t('news.months.august'),
            t('news.months.september'),
            t('news.months.october'),
            t('news.months.november'),
            t('news.months.december')
          ];
          const formattedArchives = archivesData.map(arch => ({
            ...arch,
            label: arch.year && arch.month 
              ? `${arch.year} · ${monthNames[arch.month - 1]}`
              : arch.label
          }));
          const archivesList = [
            { key: 'all', label: t('news.archives.all', 'Toutes les dates') },
            ...formattedArchives
          ];
          setArchives(archivesList);
          
          // Récupérer les catégories depuis la base de données
          const categoriesData = await getNewsCategories({ status: 'Publié' });
          setCategories(categoriesData);
          
          // Initialize likes
      setLikes(Math.floor(Math.random() * 100) + 20);
          
          // Récupérer les commentaires depuis l'API (seulement les approuvés)
          try {
            const commentsData = await getComments({ news_id: id, status: 'Approuvé', include_replies: true });
            // Transformer les commentaires de l'API vers le format attendu par le frontend
            const transformedComments = await Promise.all(commentsData.map(async (comment) => {
              // Vérifier si l'utilisateur a liké ce commentaire
              const likeStatus = await checkCommentLike(comment.id);
              
              return {
                id: comment.id,
                author: comment.author || 'Anonyme',
                content: comment.content || '',
                date: comment.created_at ? comment.created_at.split('T')[0] : (comment.date || ''),
                likes: comment.likes_count || 0,
                isLiked: likeStatus?.liked || false,
                status: comment.status || 'Approuvé',
                replies: await Promise.all((comment.replies || [])
                  .filter(reply => reply.status === 'Approuvé') // Filtrer seulement les réponses approuvées
                  .map(async (reply) => {
                    const replyLikeStatus = await checkCommentLike(reply.id);
                    return {
                      id: reply.id,
                      author: reply.author || 'Anonyme',
                      content: reply.content || '',
                      date: reply.created_at ? reply.created_at.split('T')[0] : (reply.date || ''),
                      likes: reply.likes_count || 0,
                      isLiked: replyLikeStatus?.liked || false,
                      status: reply.status || 'Approuvé'
                    };
                  }))
              };
            }));
            setComments(transformedComments);
          } catch (err) {
            setComments([]);
          }
    } else {
      // Redirect to news page if post not found
      navigate('/news');
    }
      } catch (err) {
        setError(t('newsDetail.loading', 'Chargement de l\'article...'));
        // Redirect to news page on error
        setTimeout(() => navigate('/news'), 2000);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchNewsDetail();
    }
  }, [id, navigate, t]);

  // Carousel functions
  const nextImage = () => {
    if (post && post.gallery && post.gallery.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === post.gallery.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (post && post.gallery && post.gallery.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? post.gallery.length - 1 : prevIndex - 1
      );
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Fonctions de gestion des filtres
  const handleArchiveToggle = (archiveKey) => {
    if (archiveKey === 'all') {
      navigate('/news');
    } else {
      navigate(`/news?archive=${archiveKey}`);
    }
  };

  const handleCategoryToggle = (categoryKey) => {
    if (categoryKey === 'all') {
      navigate('/news');
    } else {
      navigate(`/news?category=${categoryKey}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Si c'est déjà au format YYYY-MM-DD, le retourner tel quel
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    // Sinon, extraire seulement la partie date
    try {
    const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      // Si c'est une chaîne au format ISO, extraire seulement la partie date
      if (typeof dateString === 'string' && dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      return '';
    }
  };

  const handleBackToNews = () => {
    navigate('/news');
  };

  const handleLike = () => {
    if (isLiked) {
      setLikes(likes - 1);
      setIsLiked(false);
    } else {
      setLikes(likes + 1);
      setIsLiked(true);
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post.title;
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const copyToClipboard = (text) => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).then(() => {
        setNotification({ message: t('newsDetail.linkCopied', 'Link copied to clipboard!'), type: 'success' });
        setTimeout(() => setNotification(null), 3000);
      }).catch(() => {
        // Fallback if clipboard API fails
        fallbackCopyToClipboard(text);
      });
    } else {
      // Fallback for browsers that don't support clipboard API
      fallbackCopyToClipboard(text);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setNotification({ message: t('newsDetail.linkCopied', 'Link copied to clipboard!'), type: 'success' });
        setTimeout(() => setNotification(null), 3000);
      } else {
        setNotification({ message: t('newsDetail.copyFailed', 'Failed to copy link. Please copy manually.'), type: 'error' });
        setTimeout(() => setNotification(null), 3000);
      }
    } catch (err) {
      setNotification({ message: t('news.copyFailed', 'Failed to copy link. Please copy manually.'), type: 'error' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  // Fonction de validation d'email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation en temps réel pour l'email du commentaire principal
  const handleCommentEmailChange = (e) => {
    const email = e.target.value;
    setCommentEmail(email);
    // Réinitialiser l'erreur si l'email devient valide
    if (commentEmailError && isValidEmail(email)) {
      setCommentEmailError(false);
    } else if (email.trim() !== '' && !isValidEmail(email)) {
      setCommentEmailError(true);
    }
  };

  // Validation en temps réel pour l'email de la réponse
  const handleReplyEmailChange = (e) => {
    const email = e.target.value;
    setReplyEmail(email);
    // Réinitialiser l'erreur si l'email devient valide
    if (replyEmailError && isValidEmail(email)) {
      setReplyEmailError(false);
    } else if (email.trim() !== '' && !isValidEmail(email)) {
      setReplyEmailError(true);
    }
  };

  const handleAddComment = async () => {
    if (newComment.trim() && commentName.trim() && commentEmail.trim()) {
      if (!isValidEmail(commentEmail)) {
        setCommentEmailError(true);
        return;
      }
      
      try {
        // Créer le commentaire via l'API
        const newCommentData = await createComment({
          author: commentName,
          email: commentEmail,
          content: newComment,
          news_id: id,
          event_id: null,
          status: 'Approuvé' // Les nouveaux commentaires sont approuvés automatiquement
        });
        
        // Ajouter le nouveau commentaire à la liste immédiatement
        const newCommentObj = {
          id: newCommentData.id,
          author: newCommentData.author || commentName,
          content: newCommentData.content || newComment,
          date: newCommentData.created_at ? newCommentData.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
          likes: 0,
          isLiked: false,
          replies: []
        };
        setComments(prevComments => [newCommentObj, ...prevComments]);
        
        // Réinitialiser les champs
        setNewComment('');
        setCommentName('');
        setCommentEmail('');
        setCommentEmailError(false);
        
        // Afficher un message de succès
        setNotification({
          type: 'success',
          message: t('newsDetail.commentSuccess', 'Votre commentaire a été ajouté avec succès.')
        });
        // Masquer la notification après 5 secondes
        setTimeout(() => {
          setNotification(null);
        }, 5000);
      } catch (err) {
        setNotification({
          type: 'error',
          message: t('newsDetail.commentError', 'Erreur lors de l\'ajout du commentaire. Veuillez réessayer.')
        });
        // Masquer la notification après 5 secondes
        setTimeout(() => {
          setNotification(null);
        }, 5000);
      }
    }
  };

  const handleCommentLike = async (commentId, isReply = false, parentId = null) => {
    try {
      const result = await toggleCommentLike(commentId);
      if (result) {
        setComments(prevComments => {
          return prevComments.map(comment => {
            if (isReply && comment.id === parentId) {
              return {
                ...comment,
                replies: comment.replies.map(reply => 
                  reply.id === commentId 
                    ? { 
                        ...reply, 
                        likes: result.likes_count !== undefined ? result.likes_count : reply.likes,
                        isLiked: result.liked !== undefined ? result.liked : !reply.isLiked
                      }
                    : reply
                )
              };
            } else if (!isReply && comment.id === commentId) {
              return {
                ...comment,
                likes: result.likes_count !== undefined ? result.likes_count : comment.likes,
                isLiked: result.liked !== undefined ? result.liked : !comment.isLiked
              };
            }
            return comment;
          });
        });
      }
    } catch (err) {
    }
  };

  const handleReply = (commentId) => {
    setReplyingTo(commentId);
    setReplyText('');
    setReplyName('');
    setReplyEmail('');
    setReplyEmailError(false);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
    setReplyName('');
    setReplyEmail('');
    setReplyEmailError(false);
  };

  const handleAddReply = async (parentCommentId) => {
    if (replyText.trim() && replyName.trim() && replyEmail.trim()) {
      if (!isValidEmail(replyEmail)) {
        setReplyEmailError(true);
        return;
      }
      
      try {
        // Créer la réponse via l'API
        const newReplyData = await createComment({
          author: replyName,
          email: replyEmail,
          content: replyText,
          news_id: null, // Sera récupéré depuis le commentaire parent
          event_id: null, // Sera récupéré depuis le commentaire parent
          parent_id: parentCommentId,
          status: 'Approuvé' // Les nouvelles réponses sont approuvées automatiquement
        });
        
        // Ajouter la réponse au commentaire parent immédiatement
        const newReplyObj = {
          id: newReplyData.id,
          author: newReplyData.author || replyName,
          content: newReplyData.content || replyText,
          date: newReplyData.created_at ? newReplyData.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
          likes: 0,
          isLiked: false
        };
        
        setComments(prevComments => 
          prevComments.map(comment => 
            comment.id === parentCommentId
              ? { ...comment, replies: [...(comment.replies || []), newReplyObj] }
              : comment
          )
        );
        
        // Réinitialiser les champs
        setReplyText('');
        setReplyName('');
        setReplyEmail('');
        setReplyEmailError(false);
        setReplyingTo(null);
        
        // Afficher un message de succès
        setNotification({
          type: 'success',
          message: 'Votre réponse a été ajoutée avec succès.'
        });
        setTimeout(() => {
          setNotification(null);
        }, 5000);
      } catch (err) {
        setNotification({
          type: 'error',
          message: t('newsDetail.replyError', 'Erreur lors de l\'ajout de la réponse. Veuillez réessayer.')
        });
        setTimeout(() => {
          setNotification(null);
        }, 5000);
      }
    }
  };

  // Pagination logic for similar posts
  const itemsPerPage = 3;
  const totalSimilarPages = Math.ceil(similarPosts.length / itemsPerPage);
  const startSimilarIndex = (currentSimilarPage - 1) * itemsPerPage;
  const endSimilarIndex = startSimilarIndex + itemsPerPage;
  const currentSimilarPosts = similarPosts.slice(startSimilarIndex, endSimilarIndex);

  const handleSimilarPageChange = (page) => {
    setCurrentSimilarPage(page);
  };

  if (isLoading) {
    return (
      <div>
        <Navbar />
        <div style={{ 
          minHeight: '80vh', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '50px',
          textAlign: 'center'
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', marginBottom: '20px', color: '#96ca2d' }}></i>
          <p style={{ fontSize: '18px', fontWeight: '500', color: '#6c757d' }}>{t('newsDetail.loading', 'Chargement de l\'article...')}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div>
        <Navbar />
        <div style={{ 
          minHeight: '80vh', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '50px',
          textAlign: 'center'
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', marginBottom: '20px', color: '#dc2626' }}></i>
          <h2 style={{ color: '#dc2626', marginBottom: '20px' }}>{error || t('newsDetail.error', 'Article non trouvé')}</h2>
          <button onClick={handleBackToNews} style={{
            padding: '12px 24px',
            backgroundColor: '#96ca2d',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            {t('newsDetail.backToNews', 'Retour aux actualités')}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      {/* Notification Popup */}
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            backgroundColor: notification.type === 'success' ? '#10b981' : '#ef4444',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            minWidth: '300px',
            maxWidth: '500px',
            animation: 'slideInRight 0.3s ease-out',
            border: '2px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {notification.type === 'success' ? (
              <i className="fas fa-check" style={{ fontSize: '14px' }}></i>
            ) : (
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '14px' }}></i>
            )}
          </div>
          <div style={{ flex: 1, fontSize: '14px', fontWeight: 500, lineHeight: '1.5' }}>
            {notification.message}
          </div>
          <button
            onClick={() => setNotification(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
              transition: 'opacity 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
          >
            <i className="fas fa-times" style={{ fontSize: '16px' }}></i>
          </button>
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}
      {/* Mobile Filter Popup */}
      {isMobileFilterOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: '80px',
          right: 0,
          bottom: 0,
          background: '#ffffff',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
               padding: '10px 20px',
               borderBottom: '1px solid #e5e7eb',
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'center',
               background: '#96ca2d',
               color: '#ffffff',
               flexShrink: 0
          }}>
            <h3 style={{ paddingTop:"10px", fontSize: '20px', fontWeight: 'bold', color: '#ffffff' }}>
              {t('news.filters.title', 'Filtres')}
            </h3>
            <button onClick={() => setIsMobileFilterOpen(false)} style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: '#ffffff',
              padding: '8px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '16px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Contenu des filtres */}
          <div style={{ 
            padding: '20px', 
            flex: 1, 
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Articles récents */}
            <div style={{ marginBottom:0 }}>
              <h4 className="results-title" style={{ fontSize: "1.1rem", marginBottom: 20, fontWeight: 'bold' }}>{t('news.recent', 'Articles récents')}</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
                {relatedPosts.slice(0,5).map(p => (
                  <li key={p.id} style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 8, alignItems: 'center' }}>
                    <img src={p.img} alt={p.title} style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                    <div>
                      <a 
                        href="#" 
                        onClick={(e) => { e.preventDefault(); scrollToTop(); navigate(`/news/${p.id}`); }}
                        style={{ 
                          fontSize: '1.1rem',
                          color: '#212529', 
                          textDecoration: 'none',
                          transition: 'color 0.2s ease, text-decoration 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#96ca2d';
                          e.target.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#212529';
                          e.target.style.textDecoration = 'none';
                        }}
                        onFocus={(e) => {
                          e.target.style.color = '#96ca2d';
                          e.target.style.textDecoration = 'underline';
                        }}
                        onBlur={(e) => {
                          e.target.style.color = '#212529';
                          e.target.style.textDecoration = 'none';
                        }}
                      >
                        {p.title}
                      </a>
                      <div style={{ fontSize: 12, color: '#6c757d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fas fa-calendar-alt" style={{ fontSize: '0.7rem', color: '#6c757d' }}></i>
                        <span>{formatDate(p.date)}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Archives */}
            <div style={{ marginBottom: 0 }}>
              <h4 className="results-title" style={{ fontSize: 18, marginBottom: 8, fontWeight: 'bold' }}>{t('news.archives.title', 'Archives')}</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 4 }}>
                {archives.map(a => (
                  <li key={a.key}>
                    <span 
                      style={{ 
                        display: 'block', 
                        color: '#6c757d', 
                        cursor: 'pointer', 
                        paddingLeft: '0px', 
                        fontSize: '15px',
                        textDecoration: 'none',
                        transition: 'color 0.2s ease, text-decoration 0.2s ease'
                      }} 
                      onClick={() => handleArchiveToggle(a.key)}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#96ca2d';
                        e.target.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#6c757d';
                        e.target.style.textDecoration = 'none';
                      }}
                      onFocus={(e) => {
                        e.target.style.color = '#96ca2d';
                        e.target.style.textDecoration = 'underline';
                      }}
                      onBlur={(e) => {
                        e.target.style.color = '#6c757d';
                        e.target.style.textDecoration = 'none';
                      }}
                    >
                      {a.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Catégories */}
            <div className="widget" style={{ marginBottom: 12 }}>
              <h4 className="results-title" style={{ fontSize: 18, marginBottom: 8, fontWeight: 'bold' }}>{t('news.categories.title', 'Catégories')}</h4>
              <div className="category-filters" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', marginBottom: 8, gap: 4 }}>
                <span
                  onClick={() => handleCategoryToggle('all')}
                  style={{ 
                    width: '100%', 
                    textAlign: 'left', 
                    color: '#6c757d', 
                    cursor: 'pointer', 
                    paddingLeft: '12px', 
                    fontSize: '15px',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease, text-decoration 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#96ca2d';
                    e.target.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#6c757d';
                    e.target.style.textDecoration = 'none';
                  }}
                  onFocus={(e) => {
                    e.target.style.color = '#96ca2d';
                    e.target.style.textDecoration = 'underline';
                  }}
                  onBlur={(e) => {
                    e.target.style.color = '#6c757d';
                    e.target.style.textDecoration = 'none';
                  }}
                >
                  {t('news.categories.all', 'Toutes')}
                </span>
                {categories.map(cat => (
                  <span
                    key={cat.id}
                    onClick={() => handleCategoryToggle(cat.id)}
                    style={{ 
                      width: '100%', 
                      textAlign: 'left', 
                      color: '#6c757d', 
                      cursor: 'pointer', 
                      paddingLeft: '12px', 
                      fontSize: '15px',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease, text-decoration 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#96ca2d';
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#6c757d';
                      e.target.style.textDecoration = 'none';
                    }}
                    onFocus={(e) => {
                      e.target.style.color = '#96ca2d';
                      e.target.style.textDecoration = 'underline';
                    }}
                    onBlur={(e) => {
                      e.target.style.color = '#6c757d';
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    {cat.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="home page-template page-template-page-templates page-template-fullwidth-content page-template-page-templatesfullwidth-content-php page page-id-4059 jet-theme-core jet-theme-core--has-template jet-theme-core--has-header jet-theme-core--has-body jet-theme-core--has-footer layout-fullwidth blog-masonry elementor-default elementor-kit-5747 elementor-page elementor-page-4059">
        <div id="jet-theme-core-document" className="jet-theme-core-document jet-theme-core-document--elementor-content-type">
          <div className="jet-theme-core-document__inner">
            {!isMobileFilterOpen && <Navbar />}
          
          <article id="post-4059" className="post-4059 page type-page status-publish hentry">	
            <div data-elementor-type="wp-page" data-elementor-id="4059" className="elementor elementor-4059">
              <section className="elementor-section elementor-top-section elementor-element elementor-element-ic2p76x elementor-section-full_width elementor-section-height-default elementor-section-height-default" data-id="ic2p76x" data-element_type="section" data-settings="{&quot;jet_parallax_layout_list&quot;:[{&quot;jet_parallax_layout_image&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;_id&quot;:&quot;5759a04&quot;,&quot;jet_parallax_layout_image_tablet&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;jet_parallax_layout_image_mobile&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;jet_parallax_layout_speed&quot;:{&quot;unit&quot;:&quot;%&quot;,&quot;size&quot;:50,&quot;sizes&quot;:[]},&quot;jet_parallax_layout_type&quot;:&quot;scroll&quot;,&quot;jet_parallax_layout_direction&quot;:null,&quot;jet_parallax_layout_fx_direction&quot;:null,&quot;jet_parallax_layout_z_index&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_x&quot;:50,&quot;jet_parallax_layout_bg_x_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_x_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_y&quot;:50,&quot;jet_parallax_layout_bg_y_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_y_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_size&quot;:&quot;auto&quot;,&quot;jet_parallax_layout_bg_size_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_size_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_animation_prop&quot;:&quot;transform&quot;,&quot;jet_parallax_layout_on&quot;:[&quot;desktop&quot;,&quot;tablet&quot;]}]}">
                <div className="elementor-container elementor-column-gap-default">
                  <div className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-im9c73l" data-id="im9c73l" data-element_type="column">
                    <div className="elementor-widget-wrap elementor-element-populated">
                      <div className="elementor-element elementor-element-0bd4fc3 elementor-widget elementor-widget-jet-slider" data-id="0bd4fc3" data-element_type="widget" data-settings="{&quot;slider_height&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:852,&quot;sizes&quot;:[]},&quot;_animation&quot;:&quot;none&quot;,&quot;slider_height_tablet&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:650,&quot;sizes&quot;:[]},&quot;slider_height_mobile&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:500,&quot;sizes&quot;:[]}}" data-widget_type="jet-slider.default">
                        <div className="elementor-widget-container">
                          <div className="elementor-jet-slider jet-elements">
                            <div style={{ position: 'relative', width: '100%', height: '500px', overflow: 'hidden' }}>
                              <img src={post.img} alt="News hero" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.45)' }} />
                              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '16px', paddingBottom: '40px' }}>
                                <div style={{ background: 'rgba(0,0,0,0.45)', padding: '16px 24px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.25)', backdropFilter: 'blur(2px)', maxWidth: '960px', width: '100%' }}>
                                  <h1 style={{ color: '#fff', fontSize: 'clamp(28px,5vw,46px)', fontWeight: 800, margin: 0, textAlign: 'center', letterSpacing: '0.5px' }}>{post.title}</h1>
                                  
                                  {/* Meta info */}
                                  <div style={{ 
                                    color: 'rgba(255,255,255,0.95)', 
                                    fontSize: 'clamp(14px,2.5vw,18px)', 
                                    lineHeight: 1.6, 
                                    margin: '12px auto 40px', 
                                    textAlign: 'center', 
                                    maxWidth: '800px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '20px',
                                    flexWrap: 'wrap'
                                  }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <i className="fas fa-calendar-alt" style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.95)' }}></i>
                                    <span>{formatDate(post.date)}</span>
                                    </span>
                                   
                                    <span>{post.views} vues</span>
                                  </div>
                                  
                                  
                                  {/* Categories */}
                                  <div style={{ marginTop: '20px', textAlign: 'center' }}>
                                    {post.categories.map(category => (
                                      <span
                                        key={category}
                                        onClick={() => navigate(`/news?category=${category}`)}
                                        style={{
                                          display: 'inline-block',
                                          backgroundColor: 'rgba(150, 202, 45, 0.9)',
                                          color: 'white',
                                          padding: '8px 16px',
                                          borderRadius: '20px',
                                          fontSize: '0.9rem',
                                          marginRight: '10px',
                                          marginBottom: '10px',
                                          fontWeight: '600',
                                          cursor: 'pointer',
                                          transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor = 'rgba(150, 202, 45, 1)';
                                          e.target.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = 'rgba(150, 202, 45, 0.9)';
                                          e.target.style.transform = 'scale(1)';
                                        }}
                                      >
                                        {t(`news.categories.${category}`, category.charAt(0).toUpperCase() + category.slice(1))}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </article>
          

      {/* Main Content - Same layout as News page */}
      <section className="news-detail-content" style={{ marginTop: '40px' }}>
        <div className="container" style={{ 
          display: 'grid', 
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 320px', 
          gap: window.innerWidth <= 768 ? '16px' : '24px',
          padding: window.innerWidth <= 768 ? '16px' : '0',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {/* Main Article */}
          <main>
            {/* Mobile Filter Button */}
            {window.innerWidth <= 768 && (
              <div className="mobile-controls" style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                padding: '0 20px',
                marginBottom: '20px'
              }}>
                {/* Section Filtres - Centré */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <button
                    onClick={() => setIsMobileFilterOpen(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      borderRadius: '30px',
                      background: '#96ca2d',
                      color: '#ffffff',
                      border: 'none',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#7ba818';
                      e.target.style.transform = 'scale(1.05)';
                      e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#96ca2d';
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                    }}
                    title={t('news.filters.open', 'Ouvrir les filtres')}
                  >
                    <i className="fas fa-filter"></i>
                    <span>{t('news.filters.open', 'Filtres')}</span>
                  </button>
                </div>
              </div>
            )}

            <article className="news-card" style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              marginBottom: '30px'
            }}>
              {/* Article Content */}
              <div style={{ padding: '20px 20px' }}>
                <div 
                  style={{ 
                    fontSize: '1.1rem',
                    lineHeight: '1.8',
                    color: '#333',
                    marginBottom: '30px'
                  }}
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* Media Carousel */}
                {post.gallery && post.gallery.length > 0 && (
                  <div style={{ marginTop: '40px' }}>
                    {/* Carousel (1+ media) */}
                    {post.gallery.length >= 1 && (
                      <div style={{ position: 'relative' }}>
                        {/* Main Media */}
                        <div style={{ 
                          position: 'relative', 
                          height: '400px', 
                          overflow: 'hidden',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '8px'
                        }}>
                          {post.gallery[currentImageIndex].type === 'video' ? (
                            post.gallery[currentImageIndex].url && (post.gallery[currentImageIndex].url.includes('youtube.com') || post.gallery[currentImageIndex].url.includes('youtu.be')) ? (
                            <iframe
                                src={convertYouTubeUrl(post.gallery[currentImageIndex].url)}
                              width="100%"
                              height="100%"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              style={{ borderRadius: '8px' }}
                            ></iframe>
                            ) : (
                              <video
                                src={post.gallery[currentImageIndex].url}
                                controls
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  borderRadius: '8px',
                                  objectFit: 'contain',
                                  backgroundColor: '#000'
                                }}
                              >
                                Votre navigateur ne supporte pas la lecture de vidéos.
                              </video>
                            )
                          ) : (
                            <img 
                              src={post.gallery[currentImageIndex].url} 
                              alt={`Image ${currentImageIndex + 1}`}
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                transition: 'opacity 0.3s ease'
                              }} 
                            />
                          )}
                          
                          {/* Navigation Arrows (only if multiple media) */}
                          {post.gallery.length > 1 && (
                            <>
                              <button
                                onClick={prevImage}
                                style={{
                                  position: 'absolute',
                                  left: '15px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '50px',
                                  height: '50px',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  zIndex: 2
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                                  e.target.style.transform = 'translateY(-50%) scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                                  e.target.style.transform = 'translateY(-50%) scale(1)';
                                }}
                              >
                                ‹
                              </button>
                              
                              <button
                                onClick={nextImage}
                                style={{
                                  position: 'absolute',
                                  right: '15px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '50px',
                                  height: '50px',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  zIndex: 2
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
                                  e.target.style.transform = 'translateY(-50%) scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                                  e.target.style.transform = 'translateY(-50%) scale(1)';
                                }}
                              >
                                ›
                              </button>
                            </>
                          )}
                          
                          {/* Media Counter (only if multiple media) */}
                          {post.gallery.length > 1 && (
                            <div style={{
                              position: 'absolute',
                              bottom: '15px',
                              right: '15px',
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              padding: '8px 12px',
                              borderRadius: '20px',
                              fontSize: '0.9rem',
                              fontWeight: '600'
                            }}>
                              {currentImageIndex + 1} / {post.gallery.length}
                            </div>
                          )}
                          
                          {/* Media Type Indicator */}
                          <div style={{
                            position: 'absolute',
                            top: '15px',
                            left: '15px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            padding: '6px 10px',
                            borderRadius: '15px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            {post.gallery[currentImageIndex].type === 'video' ? (
                              <>
                                <span>▶️</span>
                                Video
                              </>
                            ) : (
                              <>
                                <span>📷</span>
                                Image
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Thumbnail Navigation */}
                        <div style={{ 
                          marginTop: '20px',
                          display: 'flex',
                          gap: '10px',
                          justifyContent: 'center',
                          flexWrap: 'wrap'
                        }}>
                          {post.gallery.map((media, index) => (
                            <button
                              key={index}
                              onClick={() => goToImage(index)}
                              style={{
                                width: '80px',
                                height: '60px',
                                border: 'none',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                padding: 0,
                                backgroundColor: 'transparent',
                                transition: 'all 0.3s ease',
                                position: 'relative',
                                outline: currentImageIndex === index ? '3px solid #96ca2d' : 'none'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                if (currentImageIndex !== index) {
                                  e.target.style.transform = 'scale(1)';
                                }
                              }}
                            >
                              <img 
                                src={media.type === 'video' ? (media.thumbnail || 'https://via.placeholder.com/80x60/000000/FFFFFF?text=Video') : media.url} 
                                alt={`Thumbnail ${index + 1}`}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'cover',
                                  opacity: currentImageIndex === index ? 1 : 0.7,
                                  transition: 'opacity 0.3s ease'
                                }} 
                                onError={(e) => {
                                  // Si l'image échoue, utiliser une image par défaut
                                  e.target.src = 'https://via.placeholder.com/80x60/000000/FFFFFF?text=Video';
                                }} 
                              />
                              {media.type === 'video' && (
                                <div style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px',
                                  zIndex: 2,
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
                                  pointerEvents: 'none'
                                }}>
                                  ▶
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Share Buttons */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '20px 0',
                  borderTop: '2px solid #e5e7eb',
                  borderBottom: '2px solid #e5e7eb',
                  marginTop: '30px',
                  marginBottom: '30px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#1f2937',
                    marginRight: '8px'
                  }}>
                    <i className="fas fa-share-alt" style={{ marginRight: '8px', color: '#96ca2d' }}></i>
                    {t('newsDetail.share', 'Share')}:
                  </span>
                  <button
                    onClick={() => handleShare('facebook')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      backgroundColor: '#1877f2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#166fe5';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#1877f2';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="fab fa-facebook-f"></i>
                    Facebook
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      backgroundColor: '#1da1f2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#1a91da';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#1da1f2';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="fab fa-twitter"></i>
                    Twitter
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      backgroundColor: '#0077b5',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#006399';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#0077b5';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="fab fa-linkedin-in"></i>
                    LinkedIn
                  </button>
                  <button
                    onClick={() => copyToClipboard(window.location.href)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#4b5563';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#6b7280';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="fas fa-link"></i>
                    {t('newsDetail.copyLink', 'Copy Link')}
                  </button>
                </div>

                 {/* Social Interactions */}
                 <div >
                   {/* Like Button */}
                  

                 

                    {/* Comments Section */}
                    <div style={{ 
                      marginTop: '30px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '0',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ 
                        backgroundColor: 'white',
                        padding: '20px',
                        borderBottom: '1px solid #e9ecef'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between'
                        }}>
                          <h3 style={{ 
                            margin: 0, 
                            fontSize: '1.3rem', 
                            fontWeight: '600',
                            color: '#2c3e50',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                          }}>
                            <span style={{ fontSize: '1.2rem' }}>💬</span>
                            {t('newsDetail.comments', 'Commentaires')} ({comments.length})
                          </h3>
                          <button 
                            onClick={() => setShowComments(!showComments)}
                            style={{
                              width: '36px',
                              height: '36px',
                              backgroundColor: showComments ? '#96ca2d' : '#f8f9fa',
                              color: showComments ? 'white' : '#6c757d',
                              border: '1px solid #e9ecef',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '1rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = showComments ? '#7ba818' : '#e9ecef';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = showComments ? '#96ca2d' : '#f8f9fa';
                            }}
                          >
                            {showComments ? '−' : '+'}
                          </button>
                        </div>
                      </div>

                      {showComments && (
                        <div style={{ 
                          backgroundColor: 'white',
                          padding: '20px'
                        }}>
                          {/* Add Comment */}
                          <div style={{ 
                            marginBottom: '25px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            padding: '20px',
                            border: '1px solid #e9ecef'
                          }}>
                            <h4 style={{ 
                              margin: '0 0 15px 0', 
                              fontSize: '1rem', 
                              fontWeight: '600', 
                              color: '#2c3e50'
                            }}>
                              {t('newsDetail.leaveComment', 'Leave a Comment')}
                            </h4>
                            <p style={{
                              margin: '0 0 20px 0',
                              fontSize: '0.85rem',
                              color: '#6c757d',
                              lineHeight: '1.4'
                            }}>
                              {t('newsDetail.emailPrivacy', 'Your email address will not be published. Required fields are marked *')}
                            </p>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', 
                              gap: '15px', 
                              marginBottom: commentEmailError ? '25px' : '15px' 
                            }}>
                              <input
                                type="text"
                                value={commentName}
                                onChange={(e) => setCommentName(e.target.value)}
                                placeholder={t('newsDetail.name', 'Name') + '*'}
                                style={{
                                  padding: '12px 15px',
                                  border: '1px solid #e9ecef',
                                  borderRadius: '6px',
                                  fontSize: '0.9rem',
                                  transition: 'all 0.3s ease',
                                  outline: 'none',
                                  backgroundColor: 'white'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#96ca2d';
                                  e.target.style.boxShadow = '0 0 0 2px rgba(150, 202, 45, 0.1)';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e9ecef';
                                  e.target.style.boxShadow = 'none';
                                }}
                              />
                              <div style={{ position: 'relative' }}>
                              <input
                                type="email"
                                value={commentEmail}
                                  onChange={handleCommentEmailChange}
                                  placeholder={t('newsDetail.email', 'Email') + '*'}
                                style={{
                                  padding: '12px 15px',
                                    border: `1px solid ${commentEmailError ? '#dc3545' : '#e9ecef'}`,
                                  borderRadius: '6px',
                                  fontSize: '0.9rem',
                                  transition: 'all 0.3s ease',
                                  outline: 'none',
                                    backgroundColor: 'white',
                                    width: '100%'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = commentEmailError ? '#dc3545' : '#96ca2d';
                                    e.target.style.boxShadow = commentEmailError ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : '0 0 0 2px rgba(150, 202, 45, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = commentEmailError ? '#dc3545' : '#e9ecef';
                                  e.target.style.boxShadow = 'none';
                                }}
                              />
                                {commentEmailError && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    fontSize: '0.75rem',
                                    color: '#dc3545',
                                    marginTop: '4px',
                                    marginBottom: '8px'
                                  }}>
                                    {t('newsDetail.invalidEmail', 'Please enter a valid email address')}
                                  </div>
                                )}
                              </div>
                            </div>
                            <textarea
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder={t('newsDetail.typeHere', 'Type here..')}
                              style={{
                                width: '100%',
                                minHeight: '100px',
                                padding: '15px',
                                border: '1px solid #e9ecef',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                resize: 'vertical',
                                marginBottom: '15px',
                                transition: 'all 0.3s ease',
                                outline: 'none',
                                fontFamily: 'inherit',
                                backgroundColor: 'white'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#96ca2d';
                                e.target.style.boxShadow = '0 0 0 2px rgba(150, 202, 45, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#e9ecef';
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                            <button 
                              onClick={handleAddComment}
                              disabled={!newComment.trim() || !commentName.trim() || !commentEmail.trim() || commentEmailError}
                              style={{
                                padding: '12px 24px',
                                backgroundColor: (newComment.trim() && commentName.trim() && commentEmail.trim() && !commentEmailError) ? '#96ca2d' : '#e9ecef',
                                color: (newComment.trim() && commentName.trim() && commentEmail.trim() && !commentEmailError) ? 'white' : '#6c757d',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: (newComment.trim() && commentName.trim() && commentEmail.trim() && !commentEmailError) ? 'pointer' : 'not-allowed',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (newComment.trim() && commentName.trim() && commentEmail.trim() && !commentEmailError) {
                                  e.target.style.backgroundColor = '#7ba818';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (newComment.trim() && commentName.trim() && commentEmail.trim() && !commentEmailError) {
                                  e.target.style.backgroundColor = '#96ca2d';
                                }
                              }}
                            >
                              {t('newsDetail.postComment', 'Post Comment')}
                            </button>
                          </div>

                          {/* Comments List */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {comments.map(comment => (
                              <div key={comment.id} style={{
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '18px',
                                border: '1px solid #e9ecef'
                              }}>
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '12px',
                                  marginBottom: '12px' 
                                }}>
                                  <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    backgroundColor: '#96ca2d',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem'
                                  }}>
                                    {comment.author.charAt(0).toUpperCase()}
                                  </div>
                                      <div>
                                      <span style={{ 
                                        fontWeight: '600', 
                                        color: '#2c3e50',
                                        fontSize: '0.95rem'
                                      }}>
                                        {comment.author}
                                      </span>
                                    <div style={{ 
                                      fontSize: '0.8rem', 
                                      color: '#6c757d',
                                      marginTop: '2px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}>
                                      <i className="fas fa-calendar-alt" style={{ fontSize: '0.7rem', color: '#6c757d' }}></i>
                                      <span>{formatDate(comment.date)}</span>
                                    </div>
                                  </div>
                                </div>
                                <p style={{ 
                                  margin: '0 0 12px 0', 
                                  color: '#555', 
                                  lineHeight: '1.6',
                                  fontSize: '0.9rem'
                                }}>
                                  {comment.content}
                                </p>
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '12px',
                                  paddingTop: '10px',
                                  borderTop: '1px solid #e9ecef'
                                }}>
                                  <button 
                                    onClick={() => handleCommentLike(comment.id)}
                                    style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 12px',
                                      backgroundColor: comment.isLiked ? '#96ca2d' : 'transparent',
                                      color: comment.isLiked ? 'white' : '#6c757d',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                      if (!comment.isLiked) {
                                    e.target.style.backgroundColor = '#96ca2d';
                                    e.target.style.borderColor = '#96ca2d';
                                    e.target.style.color = 'white';
                                      }
                                  }}
                                  onMouseLeave={(e) => {
                                      if (!comment.isLiked) {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.borderColor = '#e9ecef';
                                    e.target.style.color = '#6c757d';
                                      }
                                  }}
                                  >
                                    <span>👍</span>
                                    {comment.likes}
                                  </button>
                                  <button 
                                    onClick={() => handleReply(comment.id)}
                                    style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'transparent',
                                    color: '#6c757d',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#96ca2d';
                                    e.target.style.borderColor = '#96ca2d';
                                    e.target.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.borderColor = '#e9ecef';
                                    e.target.style.color = '#6c757d';
                                  }}
                                  >
                                    <i className="fas fa-reply" style={{ marginRight: '4px' }}></i>
                                    {t('newsDetail.reply', 'Répondre')}
                                  </button>
                                  {/* Bouton pour développer/réduire les réponses */}
                                  {comment.replies && comment.replies.length > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedReplies(prev => ({
                                          ...prev,
                                          [comment.id]: !prev[comment.id]
                                        }));
                                      }}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '6px 12px',
                                        background: expandedReplies[comment.id] 
                                          ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                                          : 'linear-gradient(135deg, #a78bfa 0%, #c084fc 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: 700,
                                        transition: 'all 0.3s ease',
                                        boxShadow: expandedReplies[comment.id] 
                                          ? '0 2px 8px rgba(99, 102, 241, 0.4)' 
                                          : '0 2px 6px rgba(167, 139, 250, 0.3)'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.background = 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
                                        e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.5)';
                                        e.target.style.transform = 'translateY(-1px)';
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!expandedReplies[comment.id]) {
                                          e.target.style.background = 'linear-gradient(135deg, #a78bfa 0%, #c084fc 100%)';
                                          e.target.style.boxShadow = '0 2px 6px rgba(167, 139, 250, 0.3)';
                                        }
                                        e.target.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      <i className={expandedReplies[comment.id] ? 'fas fa-minus' : 'fas fa-plus'} style={{ fontSize: '0.7rem' }} />
                                      <span>{comment.replies.length} {comment.replies.length === 1 ? t('newsDetail.replySingular', 'réponse') : t('newsDetail.replyPlural', 'réponses')}</span>
                                    </button>
                                  )}
                                </div>

                                {/* Reply Form */}
                                {replyingTo === comment.id && (
                                  <div style={{
                                    marginTop: '15px',
                                    padding: '15px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '1px solid #e9ecef'
                                  }}>
                                    <h5 style={{ 
                                      margin: '0 0 10px 0', 
                                      fontSize: '0.9rem', 
                                      fontWeight: '600', 
                                      color: '#2c3e50'
                                    }}>
                                      {t('newsDetail.reply', 'Répondre')} {comment.author}
                                    </h5>
                                    <div style={{ 
                                      display: 'grid', 
                                      gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', 
                                      gap: '10px', 
                                      marginBottom: replyEmailError ? '20px' : '10px' 
                                    }}>
                                      <input
                                        type="text"
                                        value={replyName}
                                        onChange={(e) => setReplyName(e.target.value)}
                                        placeholder={t('newsDetail.name', 'Name') + '*'}
                                        style={{
                                          padding: '10px 12px',
                                          border: '1px solid #e9ecef',
                                          borderRadius: '6px',
                                          fontSize: '0.85rem',
                                          transition: 'all 0.3s ease',
                                          outline: 'none',
                                          backgroundColor: 'white'
                                        }}
                                        onFocus={(e) => {
                                          e.target.style.borderColor = '#96ca2d';
                                          e.target.style.boxShadow = '0 0 0 2px rgba(150, 202, 45, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                          e.target.style.borderColor = '#e9ecef';
                                          e.target.style.boxShadow = 'none';
                                        }}
                                      />
                                      <div style={{ position: 'relative' }}>
                                        <input
                                          type="email"
                                          value={replyEmail}
                                          onChange={handleReplyEmailChange}
                                          placeholder={t('newsDetail.email', 'Email') + '*'}
                                          style={{
                                            padding: '10px 12px',
                                            border: `1px solid ${replyEmailError ? '#dc3545' : '#e9ecef'}`,
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            transition: 'all 0.3s ease',
                                            outline: 'none',
                                            backgroundColor: 'white',
                                            width: '100%'
                                          }}
                                          onFocus={(e) => {
                                            e.target.style.borderColor = replyEmailError ? '#dc3545' : '#96ca2d';
                                            e.target.style.boxShadow = replyEmailError ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : '0 0 0 2px rgba(150, 202, 45, 0.1)';
                                          }}
                                          onBlur={(e) => {
                                            e.target.style.borderColor = replyEmailError ? '#dc3545' : '#e9ecef';
                                            e.target.style.boxShadow = 'none';
                                          }}
                                        />
                                        {replyEmailError && (
                                          <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: '0',
                                            fontSize: '0.7rem',
                                            color: '#dc3545',
                                            marginTop: '4px',
                                            marginBottom: '8px'
                                          }}>
                                            {t('newsDetail.invalidEmail', 'Please enter a valid email address')}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <textarea
                                      value={replyText}
                                      onChange={(e) => setReplyText(e.target.value)}
                                      placeholder={t('newsDetail.typeHere', 'Type here..')}
                                      style={{
                                        width: '100%',
                                        minHeight: '80px',
                                        padding: '12px',
                                        border: '1px solid #e9ecef',
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        resize: 'vertical',
                                        marginBottom: '10px',
                                        transition: 'all 0.3s ease',
                                        outline: 'none',
                                        fontFamily: 'inherit',
                                        backgroundColor: 'white'
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.borderColor = '#96ca2d';
                                        e.target.style.boxShadow = '0 0 0 2px rgba(150, 202, 45, 0.1)';
                                      }}
                                      onBlur={(e) => {
                                        e.target.style.borderColor = '#e9ecef';
                                        e.target.style.boxShadow = 'none';
                                      }}
                                    />
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                      <button 
                                        onClick={() => handleAddReply(comment.id)}
                                        disabled={!replyText.trim() || !replyName.trim() || !replyEmail.trim() || replyEmailError}
                                        style={{
                                          padding: '8px 16px',
                                          backgroundColor: (replyText.trim() && replyName.trim() && replyEmail.trim() && !replyEmailError) ? '#96ca2d' : '#e9ecef',
                                          color: (replyText.trim() && replyName.trim() && replyEmail.trim() && !replyEmailError) ? 'white' : '#6c757d',
                                          border: 'none',
                                          borderRadius: '6px',
                                          cursor: (replyText.trim() && replyName.trim() && replyEmail.trim() && !replyEmailError) ? 'pointer' : 'not-allowed',
                                          fontSize: '0.8rem',
                                          fontWeight: '600',
                                          transition: 'all 0.3s ease'
                                        }}
                                      >
                                        {t('newsDetail.reply', 'Répondre')}
                                      </button>
                                      <button 
                                        onClick={handleCancelReply}
                                        style={{
                                          padding: '8px 16px',
                                          backgroundColor: 'transparent',
                                          color: '#6c757d',
                                          border: '1px solid #e9ecef',
                                          borderRadius: '6px',
                                          cursor: 'pointer',
                                          fontSize: '0.8rem',
                                          transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor = '#f8f9fa';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = 'transparent';
                                        }}
                                      >
                                        {t('newsDetail.cancel', 'Annuler')}
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Replies */}
                                {comment.replies && comment.replies.length > 0 && expandedReplies[comment.id] && (
                                  <div style={{ 
                                    marginTop: '15px', 
                                    paddingLeft: '20px',
                                    borderLeft: '3px solid #e9ecef'
                                  }}>
                                    {comment.replies.map(reply => (
                                      <div key={reply.id} style={{
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        padding: '15px',
                                        marginBottom: '10px',
                                        border: '1px solid #e9ecef'
                                      }}>
                                        <div style={{ 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          gap: '10px',
                                          marginBottom: '8px' 
                                        }}>
                                          <div style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '50%',
                                            backgroundColor: '#96ca2d',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.8rem'
                                          }}>
                                            {reply.author.charAt(0).toUpperCase()}
                                          </div>
                                          <div>
                                            <span style={{ 
                                              fontWeight: '600', 
                                              color: '#2c3e50',
                                              fontSize: '0.85rem'
                                            }}>
                                              {reply.author}
                                            </span>
                                            <div style={{ 
                                              fontSize: '0.75rem', 
                                              color: '#6c757d',
                                              marginTop: '1px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px'
                                            }}>
                                              <i className="fas fa-calendar-alt" style={{ fontSize: '0.65rem', color: '#6c757d' }}></i>
                                              <span>{formatDate(reply.date)}</span>
                                            </div>
                                          </div>
                                        </div>
                                        <p style={{ 
                                          margin: '0 0 8px 0', 
                                          color: '#555', 
                                          lineHeight: '1.5',
                                          fontSize: '0.85rem'
                                        }}>
                                          {reply.content}
                                        </p>
                                        <button 
                                          onClick={() => handleCommentLike(reply.id, true, comment.id)}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '4px 8px',
                                            backgroundColor: reply.isLiked ? '#96ca2d' : 'transparent',
                                            color: reply.isLiked ? 'white' : '#6c757d',
                                            border: '1px solid #e9ecef',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '0.75rem',
                                            transition: 'all 0.3s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            if (!reply.isLiked) {
                                              e.target.style.backgroundColor = '#96ca2d';
                                              e.target.style.borderColor = '#96ca2d';
                                              e.target.style.color = 'white';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (!reply.isLiked) {
                                              e.target.style.backgroundColor = 'transparent';
                                              e.target.style.borderColor = '#e9ecef';
                                              e.target.style.color = '#6c757d';
                                            }
                                          }}
                                        >
                                          <span>👍</span>
                                          {reply.likes}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                 </div>

                 {/* Back Link */}
                 <div style={{ textAlign: 'center', marginTop: '40px' }}>
                   <Link 
                     to="/news"
                     style={{
                       display: 'inline-flex',
                       alignItems: 'center',
                       gap: '8px',
                       padding: '12px 30px',
                       backgroundColor: '#96ca2d',
                       color: 'white',
                       textDecoration: 'none',
                       borderRadius: '25px',
                       fontSize: '1rem',
                       fontWeight: '600',
                       transition: 'all 0.3s ease'
                     }}
                     onMouseEnter={(e) => {
                       e.target.style.backgroundColor = '#7ba818';
                       e.target.style.transform = 'translateY(-2px)';
                     }}
                     onMouseLeave={(e) => {
                       e.target.style.backgroundColor = '#96ca2d';
                       e.target.style.transform = 'translateY(0)';
                     }}
                   >
                     ← {t('newsDetail.backToNews', 'Retour aux actualités')}
                   </Link>
                 </div>
              </div>
            </article>
          </main>

           {/* Sidebar - Same as News page */}
          <aside style={{ 
            display: window.innerWidth <= 768 ? 'none' : 'block',
            order: window.innerWidth <= 768 ? 1 : 2,
            background: window.innerWidth <= 768 ? '#f8f9fa' : 'transparent',
            borderRadius: window.innerWidth <= 768 ? '12px' : '0',
            padding: window.innerWidth <= 768 ? '20px' : '0',
            marginBottom: window.innerWidth <= 768 ? '20px' : '0'
          }}>
            {/* Related Posts */}
            <div style={{ marginBottom: window.innerWidth <= 768 ? '20px' : '50px' }}>
              <h4 className="results-title" style={{ fontSize: "1rem", marginBottom: 8, fontWeight: 'bold' }}>
                {t('newsDetail.recentNews', 'Actualités récentes')}
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: window.innerWidth <= 768 ? '6px' : '8px' }}>
                {relatedPosts.slice(0, window.innerWidth <= 768 ? 3 : 5).map(relatedPost => (
                  <li key={relatedPost.id} style={{ 
                    display: 'grid', 
                    gridTemplateColumns: window.innerWidth <= 768 ? '50px 1fr' : '64px 1fr', 
                    gap: window.innerWidth <= 768 ? '6px' : '8px', 
                    alignItems: 'center',
                    cursor: 'default',
                    padding: '8px',
                    borderRadius: '6px',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                  >
                    <img 
                      src={relatedPost.img} 
                      alt={relatedPost.title}
                      style={{ 
                        width: window.innerWidth <= 768 ? 50 : 64, 
                        height: window.innerWidth <= 768 ? 40 : 48, 
                        objectFit: 'cover', 
                        borderRadius: 6 
                      }} 
                    />
                    <div>
                      <a 
                        href="#" 
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollToTop(); navigate(`/news/${relatedPost.id}`); }}
                        style={{ 
                          color: '#212529', 
                          textDecoration: 'none',
                          fontSize: '1rem',
                          transition: 'color 0.2s ease, text-decoration 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#96ca2d';
                          e.target.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#212529';
                          e.target.style.textDecoration = 'none';
                        }}
                        onFocus={(e) => {
                          e.target.style.color = '#96ca2d';
                          e.target.style.textDecoration = 'underline';
                        }}
                        onBlur={(e) => {
                          e.target.style.color = '#212529';
                          e.target.style.textDecoration = 'none';
                        }}
                      >
                        {relatedPost.title}
                      </a>
                      <div style={{ 
                        fontSize: window.innerWidth <= 768 ? '0.75rem' : '12px', 
                        color: '#6c757d',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <i className="fas fa-calendar-alt" style={{ fontSize: '0.7rem', color: '#6c757d' }}></i>
                        <span>{formatDate(relatedPost.date)}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Archives */}
            <div style={{ marginBottom: window.innerWidth <= 768 ? '20px' : '50px' }}>
              <h4 className="results-title" style={{ fontSize: "1rem", marginBottom: 8, fontWeight: 'bold' }}>
                {t('news.archives.title', 'Archives')}
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 4 }}>
                {archives.map(archive => (
                  <li key={archive.key} style={{ 
                    color: '#6c757d', 
                    cursor: 'pointer', 
                    paddingLeft: '12px', 
                    fontSize: '15px',
                    transition: 'color 0.2s ease, text-decoration 0.2s ease',
                    fontWeight: '400',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#96ca2d';
                    e.target.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#6c757d';
                    e.target.style.textDecoration = 'none';
                  }}
                  onFocus={(e) => {
                    e.target.style.color = '#96ca2d';
                    e.target.style.textDecoration = 'underline';
                  }}
                  onBlur={(e) => {
                    e.target.style.color = '#6c757d';
                    e.target.style.textDecoration = 'none';
                  }}
                  onClick={() => navigate(archive.key === 'all' ? '/news' : `/news?archive=${archive.key}`)}
                  >
                    {archive.label}
                  </li>
                ))}
              </ul>
            </div>

            {/* Categories */}
            <div className="widget" style={{ marginBottom: 12 }}>
              <h4 className="results-title" style={{ fontSize: "1rem", marginBottom: 8, fontWeight: 'bold' }}>
                {t('newsDetail.categories', 'Catégories')}
              </h4>
              <div className="category-filters" style={{ 
                display: 'flex', 
                flexDirection: window.innerWidth <= 768 ? 'row' : 'column', 
                flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap',
                alignItems: 'stretch', 
                marginBottom: 8, 
                gap: window.innerWidth <= 768 ? '8px' : '4px' 
              }}>
                 <span
                   onClick={() => navigate('/news')}
                   style={{ 
                     width: window.innerWidth <= 768 ? 'auto' : '100%', 
                     textAlign: 'left', 
                     color: '#6c757d', 
                     cursor: 'pointer', 
                     padding: window.innerWidth <= 768 ? '8px 12px' : '0 0 0 12px', 
                     fontSize: window.innerWidth <= 768 ? '0.8rem' : '15px',
                     background: window.innerWidth <= 768 ? '#f8f9fa' : 'transparent',
                     borderRadius: window.innerWidth <= 768 ? '20px' : '0',
                     border: window.innerWidth <= 768 ? '1px solid #e9ecef' : 'none',
                     transition: 'all 0.3s ease, text-decoration 0.2s ease',
                     fontWeight: '400',
                     textDecoration: 'none'
                   }}
                   onMouseEnter={(e) => {
                     if (window.innerWidth <= 768) {
                       e.target.style.backgroundColor = '#e9ecef';
                     } else {
                       e.target.style.color = '#96ca2d';
                       e.target.style.textDecoration = 'underline';
                     }
                   }}
                   onMouseLeave={(e) => {
                     if (window.innerWidth <= 768) {
                       e.target.style.backgroundColor = '#f8f9fa';
                     } else {
                       e.target.style.color = '#6c757d';
                       e.target.style.textDecoration = 'none';
                     }
                   }}
                   onFocus={(e) => {
                     if (window.innerWidth <= 768) {
                       e.target.style.backgroundColor = '#e9ecef';
                     } else {
                       e.target.style.color = '#96ca2d';
                       e.target.style.textDecoration = 'underline';
                     }
                   }}
                   onBlur={(e) => {
                     if (window.innerWidth <= 768) {
                       e.target.style.backgroundColor = '#f8f9fa';
                     } else {
                       e.target.style.color = '#6c757d';
                       e.target.style.textDecoration = 'none';
                     }
                   }}
                 >
                   {t('news.categories.all', 'Toutes les catégories')}
                 </span>
                 {categories.map(cat => (
                   <span
                     key={cat.id}
                     onClick={() => navigate(`/news?category=${cat.id}`)}
                     style={{ 
                       width: window.innerWidth <= 768 ? 'auto' : '100%', 
                       textAlign: 'left', 
                       color: '#6c757d', 
                       cursor: 'pointer', 
                       padding: window.innerWidth <= 768 ? '8px 12px' : '0 0 0 12px', 
                       fontSize: window.innerWidth <= 768 ? '0.8rem' : '15px',
                       background: window.innerWidth <= 768 ? '#f8f9fa' : 'transparent',
                       borderRadius: window.innerWidth <= 768 ? '20px' : '0',
                       border: window.innerWidth <= 768 ? '1px solid #e9ecef' : 'none',
                       transition: 'all 0.3s ease, text-decoration 0.2s ease',
                       fontWeight: '400',
                       textDecoration: 'none'
                     }}
                     onMouseEnter={(e) => {
                       if (window.innerWidth <= 768) {
                         e.target.style.backgroundColor = '#e9ecef';
                       } else {
                         e.target.style.color = '#96ca2d';
                         e.target.style.textDecoration = 'underline';
                       }
                     }}
                     onMouseLeave={(e) => {
                       if (window.innerWidth <= 768) {
                         e.target.style.backgroundColor = '#f8f9fa';
                       } else {
                         e.target.style.color = '#6c757d';
                         e.target.style.textDecoration = 'none';
                       }
                     }}
                     onFocus={(e) => {
                       if (window.innerWidth <= 768) {
                         e.target.style.backgroundColor = '#e9ecef';
                       } else {
                         e.target.style.color = '#96ca2d';
                         e.target.style.textDecoration = 'underline';
                       }
                     }}
                     onBlur={(e) => {
                       if (window.innerWidth <= 768) {
                         e.target.style.backgroundColor = '#f8f9fa';
                       } else {
                         e.target.style.color = '#6c757d';
                         e.target.style.textDecoration = 'none';
                       }
                     }}
                   >
                     {cat.name}
                   </span>
                 ))}
              </div>
            </div>
          </aside>
         </div>
       </section>

       {/* Related Posts Section - News Similaires */}
       <section style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
         <div className="container" style={{ 
           maxWidth: '1200px', 
           margin: '0 auto', 
           padding: '0 20px' 
         }}>
           <h2 style={{ 
             textAlign: 'center', 
             fontSize: '1.6rem', 
             fontWeight: 'bold', 
             marginBottom: '40px',
             color: '#333'
           }}>
             {t('newsDetail.similarNews', 'Actualités similaires')}
           </h2>
           {currentSimilarPosts.length > 0 ? (
           <div className="news-grid" style={{ 
             display: 'flex', 
             flexWrap: 'wrap', 
             gap: window.innerWidth <= 768 ? '20px' : '30px', 
             justifyContent: 'center' 
           }}>
             {currentSimilarPosts.map(relatedPost => (
               <div key={relatedPost.id} className="jet-posts__item" style={{ 
                 flex: window.innerWidth <= 768 ? '1 1 100%' : '1 1 300px', 
                 maxWidth: window.innerWidth <= 768 ? '100%' : '400px' 
               }}>
                 <div 
                   className="jet-posts__inner-box" 
                   style={{
                     display: 'flex',
                     flexDirection: 'column',
                     background: '#fff',
                     border: '2px solid #e5e7eb',
                     borderRadius: '12px',
                     overflow: 'hidden',
                     boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                     height: '520px',
                     transform: 'translateY(0)',
                     transition: 'all 0.3s ease',
                    cursor: 'default',
                     position: 'relative',
                     color: 'inherit',
                     textDecoration: 'none'
                   }}
                   onMouseEnter={(e) => {
                     e.currentTarget.style.borderColor = '#96ca2d';
                     e.currentTarget.style.transform = 'translateY(-4px)';
                     e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.2)';
                   }}
                   onMouseLeave={(e) => {
                     e.currentTarget.style.borderColor = '#e5e7eb';
                     e.currentTarget.style.transform = 'translateY(0)';
                     e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                   }}
                 >
                   {/* Barre verte en haut */}
                   <div style={{
                     position: 'absolute',
                     top: '0px',
                     left: '0px',
                     right: '0px',
                     height: '3px',
                     background: 'linear-gradient(135deg, rgb(150, 202, 45) 0%, rgb(127, 184, 0) 100%)',
                     transform: 'translateY(0px)',
                     borderColor: 'rgba(150, 202, 45, 0.1)',
                     zIndex: 2
                   }} />
                   <div className="post-thumbnail" style={{ position: 'relative', height: '180px', overflow: 'hidden' }}>
                   <img 
                     src={relatedPost.img} 
                     alt={relatedPost.title}
                     style={{ 
                       width: '100%', 
                         height: '100%', 
                         objectFit: 'cover',
                         transition: 'transform 0.3s ease'
                     }} 
                   />
                   {/* Catégories sur l'image */}
                   <div style={{ 
                     position: 'absolute',
                     top: '12px', 
                     left: '12px', 
                     display: 'flex', 
                     flexWrap: 'wrap',
                     gap: '6px' 
                   }}>
                     {(relatedPost.categories || []).map((category, index) => {
                       // Afficher le nom original de la catégorie depuis la base de données
                       const categoryName = typeof category === 'string' ? category : (category.name || category);
                       return (
                         <span
                           key={index}
                           style={{
                             display: 'inline-block',
                             padding: '4px 8px',
                             background: 'rgba(150, 202, 45, 0.9)',
                             color: '#ffffff',
                             fontSize: '0.7rem',
                             fontWeight: '600',
                             borderRadius: '10px',
                             textTransform: 'uppercase',
                             letterSpacing: '0.5px',
                             backdropFilter: 'blur(4px)',
                             boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                           }}
                         >
                           {categoryName}
                         </span>
                       );
                     })}
                   </div>
                 </div>
                   <div className="jet-posts__inner-content" style={{ 
                     padding: '25px', 
                     display: 'flex', 
                     flexDirection: 'column', 
                     gap: '15px', 
                     flex: 1, 
                     height: '320px', 
                     justifyContent: 'space-between' 
                   }}>
                     <div>
                       <h4 className="entry-title" style={{ 
                         margin: '0 0 10px 0', 
                         display: '-webkit-box', 
                         WebkitLineClamp: 2, 
                         WebkitBoxOrient: 'vertical', 
                         overflow: 'hidden', 
                         textOverflow: 'ellipsis', 
                         fontSize: '1.1rem', 
                         fontWeight: 'bold',
                         height: '3rem',
                         minHeight: '3rem',
                         maxHeight: '3rem',
                         lineHeight: '1.5',
                        cursor: 'default'
                       }}
                     
                         >
                           {relatedPost.title}
                       </h4>
                       <div className="post-meta" style={{ 
                         color: '#7f8c8d', 
                         fontSize: '0.9rem', 
                         marginBottom: '15px',
                         height: '1.5rem',
                         minHeight: '1.5rem',
                         maxHeight: '1.5rem',
                         display: 'flex',
                         alignItems: 'center'
                       }}>
                         <time dateTime={relatedPost.date} title={relatedPost.date} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                           <i className="fas fa-calendar-alt" style={{ fontSize: '0.85rem', color: '#7f8c8d' }}></i>
                           <span>{formatDate(relatedPost.date)}</span>
                         </time>
                       </div>
                       
                       <div className="entry-excerpt" title={relatedPost.excerpt} style={{ 
                         color: '#34495e', 
                         fontSize: '0.95rem', 
                         lineHeight: 1.6, 
                         display: '-webkit-box', 
                         WebkitLineClamp: 4, 
                         WebkitBoxOrient: 'vertical', 
                         overflow: 'hidden', 
                         textOverflow: 'ellipsis',
                         flex: 1,
                         minHeight: '6.4rem',
                         maxHeight: '6.4rem'
                       }}>
                         {relatedPost.excerpt.length > 150 ? relatedPost.excerpt.substring(0, 150) + '...' : relatedPost.excerpt}
                       </div>
                     </div>
                     <div className="jet-more-wrap" style={{ marginTop: '5px' }}>
                       <a
                         href="#"
                         className="btn btn-primary elementor-button elementor-size-md jet-more"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollToTop(); navigate(`/news/${relatedPost.id}`); }}
                         style={{ 
                           display: 'inline-flex',
                           alignItems: 'center',
                           gap: '8px',
                           padding: '10px 20px',
                           background: '#96ca2d',
                           color: 'white',
                           textDecoration: 'none',
                           borderRadius: '25px',
                           fontWeight: '600',
                           fontSize: '0.9rem',
                           transition: 'all 0.3s ease',
                           width: 'auto',
                           textAlign: 'center'
                         }}
                         onMouseEnter={(e) => {
                           e.currentTarget.style.background = '#fff';
                           e.currentTarget.style.color = '#96ca2d';
                           e.currentTarget.style.border = '1px solid #96ca2d';
                           e.currentTarget.style.transform = 'translateX(5px)';
                         }}
                         onMouseLeave={(e) => {
                           e.currentTarget.style.background = '#96ca2d';
                           e.currentTarget.style.color = 'white';
                           e.currentTarget.style.border = 'none';
                           e.currentTarget.style.transform = 'translateX(0)';
                         }}
                       >
                         {t('newsDetail.readMore', 'Lire la suite')}
                         <i className="fas fa-arrow-right" style={{ fontSize: '0.8rem' }}></i>
                       </a>
                     </div>
                   </div>
                 </div>
               </div>
             ))}
           </div>
           ) : (
             <div style={{ 
               textAlign: 'center', 
               padding: '40px 20px',
               color: '#7f8c8d',
               fontSize: '1rem'
             }}>
               Aucune news similaire trouvée.
             </div>
           )}

           {/* Pagination */}
           {totalSimilarPages > 1 && (
             <div style={{ 
               display: 'flex', 
               justifyContent: 'center', 
               alignItems: 'center', 
               marginTop: '30px',
               border: '1px solid #e9ecef',
               borderRadius: '6px',
               overflow: 'hidden',
               width: 'fit-content',
               margin: '30px auto 0'
             }}>
               <button 
                 onClick={() => handleSimilarPageChange(currentSimilarPage - 1)}
                 disabled={currentSimilarPage === 1}
                 style={{
                   padding: '8px 12px',
                   backgroundColor: currentSimilarPage === 1 ? '#e9ecef' : '#96ca2d',
                   color: currentSimilarPage === 1 ? '#6c757d' : 'white',
                   border: 'none',
                   borderRadius: '0',
                   cursor: currentSimilarPage === 1 ? 'not-allowed' : 'pointer',
                   fontSize: '1rem',
                   fontWeight: '600',
                   transition: 'all 0.3s ease'
                 }}
               >
                 &lt;
               </button>
               
               <div style={{ display: 'flex' }}>
                 {Array.from({ length: totalSimilarPages }, (_, i) => i + 1).map(page => (
                   <button
                     key={page}
                     onClick={() => handleSimilarPageChange(page)}
                     style={{
                       padding: '8px 12px',
                       backgroundColor: page === currentSimilarPage ? '#96ca2d' : '#f8f9fa',
                       color: page === currentSimilarPage ? 'white' : '#6c757d',
                       border: 'none',
                       borderRadius: '0',
                       cursor: 'pointer',
                       fontSize: '0.9rem',
                       fontWeight: '600',
                       transition: 'all 0.3s ease'
                     }}
                   >
                     {page}
                   </button>
                 ))}
               </div>
               
               <button 
                 onClick={() => handleSimilarPageChange(currentSimilarPage + 1)}
                 disabled={currentSimilarPage === totalSimilarPages}
                 style={{
                   padding: '8px 12px',
                   backgroundColor: currentSimilarPage === totalSimilarPages ? '#e9ecef' : '#96ca2d',
                   color: currentSimilarPage === totalSimilarPages ? '#6c757d' : 'white',
                   border: 'none',
                   borderRadius: '0',
                   cursor: currentSimilarPage === totalSimilarPages ? 'not-allowed' : 'pointer',
                   fontSize: '1rem',
                   fontWeight: '600',
                   transition: 'all 0.3s ease'
                 }}
               >
                 &gt;
               </button>
             </div>
           )}
         </div>
       </section>

       <Footer />
          </div>
        </div>
      </div>
    </>
  );
};

export default NewsDetail;